/**
 * Mixin that provides a reusable helper for downloading binary (blob) HTTP responses,
 * such as PDF files, triggered programmatically.
 *
 * Usage: add `downloadBlobMixin` to the `mixins` array of your component and call
 * `this.downloadBlobResponse(response, 'fallback-name.pdf')` with the axios response
 * obtained using `{ responseType: 'blob' }`.
 *
 * The mixin returns early when {@code response} or {@code response.data} is absent,
 * so callers do not need to guard against missing responses. The MIME type falls back
 * to a value derived from the default filename's extension via {@code _mimeTypeFromFilename},
 * defaulting to `application/octet-stream` for unknown types.
 */
export const downloadBlobMixin = {
  methods: {
    /**
     * Triggers a browser file download from an axios blob response.
     *
     * Returns early if {@code response} or {@code response.data} is absent so that
     * callers do not need to guard against missing responses themselves.
     *
     * Reads the filename from the `Content-Disposition` response header when present
     * (honouring both the `filename` and `filename*` directives). Falls back to
     * {@code defaultFilename} when the header is absent or unparseable.
     *
     * The MIME type is taken from the `Content-Type` response header. When that header
     * is absent the type is derived from the file extension of {@code defaultFilename}
     * (`.pdf` → `application/pdf`, `.xlsx` → `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`,
     * `.csv` → `text/csv`). Any other extension falls back to `application/octet-stream`.
     *
     * @param {Object} response        - The axios response object (responseType: 'blob')
     * @param {string} defaultFilename - Filename to use when the header is unavailable
     */
    downloadBlobResponse (response, defaultFilename) {
      if (!response || !response.data) {
        return
      }

      const headers = response.headers || {}
      const contentType = headers['content-type'] || headers['Content-Type'] || this._mimeTypeFromFilename(defaultFilename)
      const blob = new Blob([response.data], { type: contentType })

      let filename = defaultFilename
      const contentDisposition = headers['content-disposition'] || headers['Content-Disposition']
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i)
        const filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i)
        if (filenameStarMatch && filenameStarMatch[1]) {
          const encodedFilename = filenameStarMatch[1].trim().replace(/^UTF-8''/i, '').replace(/^"(.*)"$/, '$1')
          try {
            filename = decodeURIComponent(encodedFilename)
          } catch (e) {
            filename = encodedFilename
          }
        } else if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim().replace(/^"(.*)"$/, '$1')
        }
      }

      const navigatorRef = window.navigator
      if (navigatorRef && typeof navigatorRef.msSaveOrOpenBlob === 'function') {
        navigatorRef.msSaveOrOpenBlob(blob, filename)
        return
      }
      const link = document.createElement('a')
      const url = window.URL.createObjectURL(blob)
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    /**
     * Opens a binary (blob) HTTP response in a new browser tab and revokes the
     * object URL once the tab has loaded, preventing memory leaks.
     *
     * Returns early if {@code response} or {@code response.data} is absent.
     *
     * The MIME type is resolved from the `Content-Type` response header, falling
     * back to a value derived from {@code defaultFilename}'s extension via
     * {@code _mimeTypeFromFilename}.
     *
     * @param {Object} response        - The axios response object (responseType: 'blob')
     * @param {string} defaultFilename - Used for MIME type fallback when the header is absent
     */
    openBlobResponse (response, defaultFilename) {
      if (!response || !response.data) {
        return
      }
      const headers = response.headers || {}
      const contentType = headers['content-type'] || headers['Content-Type'] || this._mimeTypeFromFilename(defaultFilename)
      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)
      const newWindow = window.open(url, '_blank')
      if (newWindow) {
        newWindow.addEventListener('load', () => window.URL.revokeObjectURL(url))
      } else {
        // Pop-up blocked — revoke immediately to avoid the leak
        window.URL.revokeObjectURL(url)
      }
    },

    /**
     * Derives a MIME type from a filename's extension.
     * Falls back to `application/octet-stream` for unknown extensions.
     *
     * @param {string} filename
     * @returns {string}
     */
    _mimeTypeFromFilename (filename) {
      if (!filename) {
        return 'application/octet-stream'
      }
      const ext = filename.split('.').pop().toLowerCase()
      const mimeTypes = {
        pdf: 'application/pdf',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        csv: 'text/csv',
        txt: 'text/plain',
        zip: 'application/zip',
        xml: 'application/xml',
        json: 'application/json'
      }
      return mimeTypes[ext] || 'application/octet-stream'
    }
  }
}
